import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { Money } from '@/api/types';
import { inZone, nowInZone } from './time';

dayjs.extend(relativeTime);

const rupees = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Money arrives as a decimal string. It is parsed *once*, here, only to be
 * grouped and symbol-prefixed for display — never to be arithmetic'd on. Any
 * sum the user will act on is computed server-side in integer paise.
 */
export function formatMoney(value: Money | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  return Number.isFinite(amount) ? rupees.format(amount) : String(value);
}

export function formatPercentage(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  return Number.isFinite(amount) ? `${amount.toFixed(2)}%` : String(value);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-IN').format(value);
}

/** Signed, so a ledger row reads as a movement rather than a quantity. */
export function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (value === 0) return '0';
  return `${value > 0 ? '+' : ''}${formatNumber(value)}`;
}

/*
 * All four read the clock in the PLATFORM's zone, not the browser's — see
 * `time.ts` for why that distinction is a defect rather than a nicety. Every
 * date in the panel goes through one of these, so this is the single place the
 * question "whose 9am?" gets answered.
 */

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return inZone(value).format('DD MMM YYYY');
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return inZone(value).format('DD MMM YYYY, HH:mm');
}

/**
 * "3 hours ago" is a distance between two instants, and a distance has no
 * timezone — so this one deliberately does NOT convert. Running it through the
 * platform zone would change nothing and only suggest that it had.
 */
export function fromNow(value: string | null | undefined): string {
  if (!value) return '—';
  return dayjs(value).fromNow();
}

/**
 * Negative once the date is past — the caller decides how loudly to say so.
 *
 * Both ends are snapped to midnight in the platform's zone. On the browser's
 * clock a batch expiring tomorrow in Kolkata could report as expiring today,
 * which for an expiry warning is the difference between sellable and not.
 */
export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null;
  return inZone(value).startOf('day').diff(nowInZone().startOf('day'), 'day');
}

export function fullName(user: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || user.phone || 'Unnamed user';
}

export function initials(user: { firstName?: string | null; lastName?: string | null }): string {
  const letters = [user.firstName?.[0], user.lastName?.[0]].filter(Boolean).join('');
  return (letters || '?').toUpperCase();
}

/**
 * `SUPER_ADMIN` → `Super admin`. The wire name, made readable.
 *
 * The account row was doing `role.replace('_', ' ').toLowerCase()` inline,
 * which replaces the FIRST underscore only — correct for the two-word roles we
 * ship today and quietly wrong for the first three-word one. Sentence case
 * rather than Title Case, because a role is a noun in a sentence here, not a
 * heading.
 */
export function roleLabel(role: string): string {
  const words = role.toLowerCase().split('_').filter(Boolean);
  const [first, ...rest] = words;
  if (!first) return role;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/** UUIDs are unreadable in a table but still need to be recognisable across rows. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.slice(0, 8);
}

/**
 * The nouns for the pack ladder.
 *
 * Stock is counted in packs everywhere, and "50" on its own means nothing —
 * 50 strips, 50 bottles and 50 tablets are three very different quantities of
 * medicine. Both lists mirror the enums on the medicines table.
 */
const PACK_WORDS: Record<string, [one: string, many: string]> = {
  STRIP: ['strip', 'strips'],
  BOTTLE: ['bottle', 'bottles'],
  TUBE: ['tube', 'tubes'],
  VIAL: ['vial', 'vials'],
  AMPOULE: ['ampoule', 'ampoules'],
  SACHET: ['sachet', 'sachets'],
  TIN: ['tin', 'tins'],
  JAR: ['jar', 'jars'],
  PACK: ['pack', 'packs'],
};

const UNIT_WORDS: Record<string, [one: string, many: string]> = {
  TABLET: ['tablet', 'tablets'],
  CAPSULE: ['capsule', 'capsules'],
  ML: ['ml', 'ml'],
  GM: ['g', 'g'],
  IU: ['IU', 'IU'],
  DROP: ['drop', 'drops'],
  SACHET: ['sachet', 'sachets'],
  PIECE: ['piece', 'pieces'],
};

export function packNoun(form: string | null | undefined, count = 1): string {
  const [one, many] = PACK_WORDS[form ?? 'PACK'] ?? PACK_WORDS.PACK!;
  return count === 1 ? one : many;
}

export function unitNoun(unit: string | null | undefined, count = 2): string {
  const words = UNIT_WORDS[unit ?? ''];
  if (!words) return count === 1 ? 'unit' : 'units';
  return count === 1 ? words[0] : words[1];
}

/** "tablets in a strip" → "Tablets in a strip". */
export function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
